import {
  advanceGame,
  createGameState,
  findNearestMatchingLetter,
  resolveLetterHit,
  resetGame,
  type GameEvent,
  type GameSettings,
  type GameState,
  type Letter,
} from './core';
import { InputManager } from './input';
import { RainRenderer } from './renderer';
import { BadgeToaster } from './toast';

type FlashVariant = 'combo' | 'warn';

const ACHIEVEMENT_DETAILS: Record<string, { title: string; description: string }> = {
  'streak-5': {
    title: 'Warm Current',
    description: 'Keep a 5-letter streak alive.',
  },
  'streak-10': {
    title: 'Flow Runner',
    description: 'Hit a streak of 10.',
  },
  'streak-12': {
    title: 'Pulse Initiate',
    description: 'Hold a 12-letter streak.',
  },
  'streak-15': {
    title: 'Storm Rider',
    description: 'Hold a 15-letter streak.',
  },
  'streak-20': {
    title: 'Skyline Glide',
    description: 'Reach a 20-letter streak.',
  },
  'streak-25': {
    title: 'Pulse Mastery',
    description: 'Hold a 25-letter streak.',
  },
  'streak-30': {
    title: 'Tempest Dancer',
    description: 'Reach a 30-letter streak.',
  },
  'streak-35': {
    title: 'Midnight Maestro',
    description: 'Chain 35 letters in a row.',
  },
  'streak-40': {
    title: 'Pulse Virtuoso',
    description: 'Maintain a 40-letter streak.',
  },
  'streak-45': {
    title: 'Stormsurge Legend',
    description: 'Survive a 45-letter streak.',
  },
};

const DEFAULT_ACHIEVEMENT = {
  title: 'Achievement unlocked',
  description: 'You reached a new streak milestone.',
};

class FrameBudgetMonitor {
  private exceedCount = 0;
  constructor(private readonly budgetMs: number, private readonly onViolation?: (dtMs: number) => void) {}

  mark(dtSeconds: number): void {
    const ms = dtSeconds * 1000;
    if (ms > this.budgetMs) {
      this.exceedCount += 1;
      if (this.exceedCount >= 12) {
        this.onViolation?.(ms);
        this.exceedCount = 0;
      }
    } else {
      this.exceedCount = 0;
    }
  }

  reset(): void {
    this.exceedCount = 0;
  }
}

export type DifficultyKey =
  | 'level1'
  | 'level2'
  | 'level3'
  | 'level4'
  | 'level5'
  | 'level6';

interface TimeSource {
  now(): number;
  raf(callback: FrameRequestCallback): number;
  cancel(id: number): void;
}

const defaultTimeSource: TimeSource = {
  now: () => (typeof performance !== 'undefined' ? performance.now() : Date.now()),
  raf: (callback) =>
    (typeof requestAnimationFrame !== 'undefined'
      ? requestAnimationFrame(callback)
      : (setTimeout(() => callback(Date.now()), 16) as unknown as number)),
  cancel: (id) => {
    if (typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(id);
    } else {
      clearTimeout(id as unknown as ReturnType<typeof setTimeout>);
    }
  },
};

export interface GameSnapshot {
  difficulty: DifficultyKey;
  streak: number;
  bestStreak: number;
  updatedAt: number;
  achievements: string[];
  lastAchievementAt: number | null;
}

export interface GameControllerOptions {
  stage: HTMLElement;
  keyboardHost: HTMLElement;
  toastHost: HTMLElement;
  scoreboard: {
    streak: HTMLElement;
    best: HTMLElement;
  };
  liveRegion: HTMLElement;
  announce: (message: string) => void;
  difficulty: DifficultyKey;
  time?: TimeSource;
  seed?: number | string;
  onRunStateChange?: (running: boolean) => void;
  onSnapshot?: (snapshot: GameSnapshot) => void;
  initialAchievements?: string[];
  frameBudgetMs?: number;
}
interface DifficultyPreset {
  key: DifficultyKey;
  label: string;
  description: string;
  settings: GameSettings;
}

const DIFFICULTY_PRESETS: DifficultyPreset[] = [
  {
    key: 'level1',
    label: 'Level 1',
    description: 'Single letter every ~2 seconds.',
    settings: {
      spawner: {
        initialInterval: 2,
        minInterval: 1.4,
        rampToMinAfter: 54,
        maxConcurrent: 1,
        columns: 5,
        initialVelocity: 0.028,
        spawnPadding: -0.015,
      },
      engine: {
        gravity: 0.18,
        terminalVelocity: 0.45,
        floorY: 1,
      },
      score: {
        basePoints: 90,
        comboThresholds: [6, 10],
        maxLives: 99,
        streakDecayThreshold: 6200,
        achievementThresholds: [5, 12, 20],
      },
    },
  },
  {
    key: 'level2',
    label: 'Level 2',
    description: 'Gentle pace with up to two letters.',
    settings: {
      spawner: {
        initialInterval: 1.6,
        minInterval: 1,
        rampToMinAfter: 46,
        maxConcurrent: 2,
        columns: 6,
        initialVelocity: 0.036,
        spawnPadding: -0.025,
      },
      engine: {
        gravity: 0.24,
        terminalVelocity: 0.56,
        floorY: 1,
      },
      score: {
        basePoints: 100,
        comboThresholds: [5, 9, 13],
        maxLives: 99,
        streakDecayThreshold: 5400,
        achievementThresholds: [6, 14, 24],
      },
    },
  },
  {
    key: 'level3',
    label: 'Level 3',
    description: 'Steady rhythm for casual play.',
    settings: {
      spawner: {
        initialInterval: 1.35,
        minInterval: 0.72,
        rampToMinAfter: 38,
        maxConcurrent: 4,
        columns: 7,
        initialVelocity: 0.053,
        spawnPadding: -0.035,
      },
      engine: {
        gravity: 0.3,
        terminalVelocity: 0.7,
        floorY: 1,
      },
      score: {
        basePoints: 110,
        comboThresholds: [4, 7, 10, 13],
        maxLives: 99,
        streakDecayThreshold: 4400,
        achievementThresholds: [8, 16, 28],
      },
    },
  },
  {
    key: 'level4',
    label: 'Level 4',
    description: 'Brisk flow with moderate density.',
    settings: {
      spawner: {
        initialInterval: 1.05,
        minInterval: 0.5,
        rampToMinAfter: 32,
        maxConcurrent: 6,
        columns: 8,
        initialVelocity: 0.07,
        spawnPadding: -0.045,
      },
      engine: {
        gravity: 0.36,
        terminalVelocity: 0.85,
        floorY: 1,
      },
      score: {
        basePoints: 125,
        comboThresholds: [4, 7, 10, 13, 16],
        maxLives: 99,
        streakDecayThreshold: 3600,
        achievementThresholds: [10, 20, 32],
      },
    },
  },
  {
    key: 'level5',
    label: 'Level 5',
    description: 'Rapid spawn intervals for quick reflexes.',
    settings: {
      spawner: {
        initialInterval: 0.88,
        minInterval: 0.36,
        rampToMinAfter: 26,
        maxConcurrent: 8,
        columns: 9,
        initialVelocity: 0.09,
        spawnPadding: -0.05,
      },
      engine: {
        gravity: 0.42,
        terminalVelocity: 0.98,
        floorY: 1,
      },
      score: {
        basePoints: 145,
        comboThresholds: [4, 7, 11, 15, 18],
        maxLives: 99,
        streakDecayThreshold: 3000,
        achievementThresholds: [12, 24, 36],
      },
    },
  },
  {
    key: 'level6',
    label: 'Level 6',
    description: 'Arcade rush with tight spacing.',
    settings: {
      spawner: {
        initialInterval: 0.72,
        minInterval: 0.26,
        rampToMinAfter: 22,
        maxConcurrent: 10,
        columns: 10,
        initialVelocity: 0.105,
        spawnPadding: -0.055,
      },
      engine: {
        gravity: 0.5,
        terminalVelocity: 1.05,
        floorY: 1,
      },
      score: {
        basePoints: 165,
        comboThresholds: [4, 7, 11, 15, 19, 23],
        maxLives: 99,
        streakDecayThreshold: 2600,
        achievementThresholds: [14, 28, 42],
      },
    },
  },
];

const PRESET_BY_KEY = new Map<DifficultyKey, DifficultyPreset>(
  DIFFICULTY_PRESETS.map((preset) => [preset.key, preset])
);

export const DIFFICULTY_PRESET_ORDER = DIFFICULTY_PRESETS.map((preset) => ({
  key: preset.key,
  label: preset.label,
  description: preset.description,
}));

const LETTER_VERTICAL_WEIGHT = 0.18;
const MAX_STEP_SECONDS = 0.12;

const FLASH_CLASS_BY_VARIANT: Record<FlashVariant, string> = {
  combo: 'streak-indicator--flash',
  warn: 'streak-indicator--warn',
};

export class GameController {
  private readonly time: TimeSource;
  private readonly scoreboard: GameControllerOptions['scoreboard'];
  private readonly liveRegion: HTMLElement;
  private readonly announce: (message: string) => void;
  private renderer: RainRenderer;
  private input: InputManager;
  private settings: GameSettings;
  private state: GameState;
  private running = false;
  private readonly onRunStateChange?: (running: boolean) => void;
  private readonly onSnapshot?: (snapshot: GameSnapshot) => void;
  private rafId: number | null = null;
  private lastTimestamp = 0;
  private difficulty: DifficultyKey;
  private readonly streakIndicator: HTMLElement | null;
  private readonly recordIndicator: HTMLElement | null;
  private readonly flashTimers = new Map<HTMLElement, number>();
  private readonly toastHost: HTMLElement;
  private readonly toaster: BadgeToaster;
  private readonly displayedAchievements = new Set<string>();
  private readonly resizeObserver?: ResizeObserver;
  private resizeCooldown = 0;
  private lastStreakText = '';
  private lastBestText = '';
  private readonly frameMonitor: FrameBudgetMonitor;
  private pendingDifficulty: DifficultyKey | null = null;
  private pendingSettings: GameSettings | null = null;
  private pendingResume = false;
  private pendingSeed: number | string | undefined;
  private lastSnapshot: GameSnapshot | null = null;

  constructor(private readonly options: GameControllerOptions) {
    this.time = options.time ?? defaultTimeSource;
    this.scoreboard = options.scoreboard;
    this.liveRegion = options.liveRegion;
    this.announce = options.announce;
    this.onRunStateChange = options.onRunStateChange;
    this.onSnapshot = options.onSnapshot;
    this.difficulty = options.difficulty;
    this.settings = cloneSettings(requirePreset(options.difficulty).settings);
    this.state = createGameState(this.settings, { seed: options.seed });

    this.renderer = new RainRenderer(options.stage, {
      floor: this.settings.engine.floorY,
      doc: options.stage.ownerDocument ?? document,
    });
    this.renderer.reconcile(this.state.letters);

    this.input = new InputManager({
      stage: options.stage,
      keyboardHost: options.keyboardHost,
      doc: options.stage.ownerDocument ?? document,
      onLetter: (letter) => this.submitLetter(letter),
    });

    this.streakIndicator = this.scoreboard.streak.closest('[data-streak-indicator]') ?? this.scoreboard.streak;
    this.recordIndicator = this.scoreboard.best.closest('[data-best-indicator]') ?? this.scoreboard.best;
    this.toastHost = options.toastHost;
    this.toaster = new BadgeToaster(this.toastHost, options.stage.ownerDocument ?? document);
    this.toastHost.innerHTML = '';
    if (options.initialAchievements) {
      options.initialAchievements.forEach((id) => this.displayedAchievements.add(id));
    }

    const budget = options.frameBudgetMs ?? 16.7;
    this.frameMonitor = new FrameBudgetMonitor(budget, (ms) => {
      console.warn(
        `Letter Rain: frame exceeded ${(budget).toFixed(1)}ms budget (dt=${ms.toFixed(2)}ms).`
      );
    });

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.resizeCooldown = 0.25;
      });
      this.resizeObserver.observe(options.stage);
    }

    this.updateScoreboard();
    this.updateLiveRegion();
  }

  start(options: { silent?: boolean } = {}): void {
    if (this.running) return;
    this.running = true;
    this.onRunStateChange?.(true);
    this.lastTimestamp = this.time.now();
    this.scheduleNextFrame();
    if (!options.silent) {
      this.announce('Game started.');
    }
  }

  pause(options: { silent?: boolean } = {}): void {
    if (!this.running) return;
    this.running = false;
    this.onRunStateChange?.(false);
    if (this.rafId !== null) {
      this.time.cancel(this.rafId);
      this.rafId = null;
    }
    if (!options.silent) {
      this.announce('Game paused.');
    }
  }

  toggleRunning(): void {
    if (this.running) {
      this.pause();
    } else {
      this.start();
    }
  }

  reset(seed?: number | string, options: { silent?: boolean } = {}): void {
    const { silent = false } = options;
    this.pause({ silent: true });
    resetGame(this.state, this.settings);
    if (seed !== undefined) {
      this.state = createGameState(this.settings, { seed });
    }
    this.renderer.clear();
    this.renderer.reconcile(this.state.letters);
    this.updateScoreboard();
    this.updateLiveRegion();
    if (!silent) {
      this.announce('Game reset.');
    }
    this.toastHost.innerHTML = '';
    this.displayedAchievements.clear();
    this.lastSnapshot = null;
    this.frameMonitor.reset();
  }

  setDifficulty(
    key: DifficultyKey,
    options: { seed?: number | string; instant?: boolean } = {}
  ): void {
    const preset = requirePreset(key);
    const nextSettings = cloneSettings(preset.settings);
    const { seed, instant = false } = options;

    if (instant) {
      this.pendingDifficulty = null;
      this.pendingSettings = null;
      this.pendingResume = false;
      this.pendingSeed = undefined;
      this.applyDifficulty(key, nextSettings, this.running, true, seed);
      return;
    }

    if (this.difficulty === key && this.pendingDifficulty === null) {
      if (seed !== undefined && this.state.letters.length === 0) {
        this.applyDifficulty(key, nextSettings, this.running, true, seed);
      }
      return;
    }

    if (this.state.letters.length === 0 && this.pendingDifficulty === null) {
      this.applyDifficulty(key, nextSettings, this.running, true, seed);
      return;
    }

    this.pendingDifficulty = key;
    this.pendingSettings = nextSettings;
    this.pendingResume = this.running;
    this.pendingSeed = seed;
    this.announce(`Difficulty will switch to ${preset.label} after this wave.`);
  }

  submitLetter(letter: string, preferredId?: string): void {
    if (!this.running) {
      this.start({ silent: true });
    }
    let targetId: string | null = null;
    if (preferredId) {
      const candidate = this.state.letters.find(
        (active) => active.id === preferredId && active.alive && active.ch === letter
      );
      if (candidate) {
        targetId = candidate.id;
      }
    }
    if (!targetId) {
      const target = findNearestMatchingLetter(this.state.letters, letter, {
        columns: this.settings.spawner.columns,
        verticalWeight: LETTER_VERTICAL_WEIGHT,
        maxDistance: 1.2,
      });
      if (!target) {
        return;
      }
      targetId = target.id;
    }

    const previousBest = this.state.score.bestStreak;
    const event = resolveLetterHit(this.state, this.settings, targetId);
    if (event?.type === 'hit') {
      this.onGameEvents([event], previousBest);
      this.renderer.reconcile(this.state.letters);
      this.updateScoreboard();
      this.updateLiveRegion();
    }
  }

  destroy(): void {
    this.pause({ silent: true });
    this.input.dispose();
    this.renderer.clear();
    for (const timer of this.flashTimers.values()) {
      window.clearTimeout(timer);
    }
    this.flashTimers.clear();
    this.toastHost.innerHTML = '';
    this.displayedAchievements.clear();
    this.lastSnapshot = null;
    this.resizeObserver?.disconnect();
    this.frameMonitor.reset();
  }

  debugStep(milliseconds: number, options: { force?: boolean } = {}): GameEvent[] {
    if (!options.force && !this.running) {
      return [];
    }
    const seconds = Math.max(0, milliseconds / 1000);
    const events = this.step(seconds);
    this.renderer.reconcile(this.state.letters);
    this.updateScoreboard();
    this.updateLiveRegion();
    return events;
  }

  debugLetters(): Array<Pick<Letter, 'id' | 'y' | 'alive'>> {
    return this.state.letters.map((letter) => ({
      id: letter.id,
      y: letter.y,
      alive: letter.alive,
    }));
  }

  getState(): GameState {
    return this.state;
  }

  getDifficulty(): DifficultyKey {
    return this.difficulty;
  }

  isRunning(): boolean {
    return this.running;
  }

  private scheduleNextFrame(): void {
    if (!this.running) {
      return;
    }
    this.rafId = this.time.raf((timestamp) => {
      if (!this.running) {
        return;
      }
      if (this.pendingDifficulty && this.state.letters.length === 0) {
        this.applyPendingDifficulty();
      }

      const now = typeof timestamp === 'number' ? timestamp : this.time.now();
      let dtSeconds = (now - this.lastTimestamp) / 1000;
      this.lastTimestamp = now;
      if (!Number.isFinite(dtSeconds)) {
        dtSeconds = 0;
      }
      if (dtSeconds > 0) {
        const cappedDt = Math.min(dtSeconds, MAX_STEP_SECONDS);
        if (this.resizeCooldown > 0) {
          this.resizeCooldown -= cappedDt;
        } else {
          this.step(cappedDt);
          this.renderer.reconcile(this.state.letters);
          this.updateScoreboard();
          this.updateLiveRegion();
        }
        this.frameMonitor.mark(cappedDt);
      }
      this.scheduleNextFrame();
    });
  }

  private step(dtSeconds: number): GameEvent[] {
    const previousBest = this.state.score.bestStreak;
    const events = advanceGame(this.state, this.settings, dtSeconds);
    this.onGameEvents(events, previousBest);
    return events;
  }

  private onGameEvents(events: readonly GameEvent[], previousBest: number): void {
    if (events.length === 0) {
      if (this.pendingDifficulty && this.state.letters.length === 0) {
        this.applyPendingDifficulty();
      }
      return;
    }
    for (const event of events) {
      if (event.type === 'miss') {
        this.renderer.triggerSplash(event.letter);
        this.flashCard(this.streakIndicator, 'warn');
        this.announce('Letter splashed. Streak reset.');
      } else if (event.type === 'hit') {
        this.renderer.triggerHit(event.letter, this.randomHitColor());
        if (event.comboIncreased) {
          this.flashCard(this.streakIndicator, 'combo');
          this.announce(`Combo x${event.score.multiplier}`);
        }
        if (event.score.bestStreak > previousBest) {
          this.flashCard(this.recordIndicator, 'combo');
        }
        this.handleAchievements(event.achievements);
      }
    }
  }

  private flashCard(card: HTMLElement | null, variant: FlashVariant): void {
    if (!card) {
      return;
    }
    const className = FLASH_CLASS_BY_VARIANT[variant];
    card.classList.add(className);
    const existing = this.flashTimers.get(card);
    if (existing !== undefined) {
      window.clearTimeout(existing);
    }
    const timeoutId = window.setTimeout(() => {
      card.classList.remove(className);
      this.flashTimers.delete(card);
    }, 520);
    this.flashTimers.set(card, timeoutId);
  }

  private randomHitColor(): string {
    const hue = Math.floor(Math.random() * 360);
    const saturation = 62 + Math.random() * 18;
    const lightness = 55 + Math.random() * 10;
    return `hsl(${hue} ${saturation}% ${lightness}%)`;
  }

  private updateScoreboard(): void {
    const streakText = this.state.score.streak.toString();
    if (streakText !== this.lastStreakText) {
      this.lastStreakText = streakText;
      this.scoreboard.streak.textContent = streakText;
    }
    const bestText = this.state.score.bestStreak.toString();
    if (bestText !== this.lastBestText) {
      this.lastBestText = bestText;
      this.scoreboard.best.textContent = bestText;
    }
    this.emitSnapshot();
  }

  private updateLiveRegion(): void {
    this.liveRegion.textContent = `Current streak ${this.state.score.streak}. Best streak ${this.state.score.bestStreak}.`;
  }

  private applyPendingDifficulty(): void {
    if (!this.pendingDifficulty || !this.pendingSettings) {
      return;
    }
    const resume = this.pendingResume || this.running;
    const seed = this.pendingSeed;
    this.applyDifficulty(this.pendingDifficulty, this.pendingSettings, resume, false, seed);
    this.pendingDifficulty = null;
    this.pendingSettings = null;
    this.pendingResume = false;
    this.pendingSeed = undefined;
  }

  private applyDifficulty(
    key: DifficultyKey,
    settings: GameSettings,
    resume: boolean,
    _announceInstant: boolean,
    seed?: number | string
  ): void {
    const preset = requirePreset(key);
    const wasRunning = this.running;
    if (wasRunning) {
      this.pause({ silent: true });
    }

    this.difficulty = key;
    this.settings = settings;
    const initOptions = seed !== undefined ? { seed } : undefined;
    this.state = createGameState(this.settings, initOptions);
    for (const timer of this.flashTimers.values()) {
      globalThis.clearTimeout(timer);
    }
    this.flashTimers.clear();
    this.renderer = new RainRenderer(this.options.stage, {
      floor: this.settings.engine.floorY,
      doc: this.options.stage.ownerDocument ?? document,
    });
    this.renderer.reconcile(this.state.letters);
    this.toastHost.innerHTML = '';
    this.displayedAchievements.clear();
    this.lastSnapshot = null;
    this.frameMonitor.reset();
    this.updateScoreboard();
    this.updateLiveRegion();
    this.announce(`Difficulty set to ${preset.label}.`);

    if (resume && wasRunning) {
      this.start({ silent: true });
    }
  }

  private emitSnapshot(): void {
    const next: GameSnapshot = {
      difficulty: this.difficulty,
      streak: this.state.score.streak,
      bestStreak: this.state.score.bestStreak,
      updatedAt: Date.now(),
      achievements: [...this.state.score.achievements.unlocked],
      lastAchievementAt: this.state.score.achievements.lastUnlockedAt,
    };
    const prev = this.lastSnapshot;
    const changed =
      !prev ||
      prev.streak !== next.streak ||
      prev.bestStreak !== next.bestStreak ||
      prev.difficulty !== next.difficulty ||
      prev.lastAchievementAt !== next.lastAchievementAt ||
      prev.achievements.length !== next.achievements.length;
    if (changed) {
      this.lastSnapshot = next;
      this.onSnapshot?.(next);
    }
  }

  private handleAchievements(ids: readonly string[]): void {
    if (!ids || ids.length === 0) {
      return;
    }
    const fresh = ids.filter((id) => !this.displayedAchievements.has(id));
    if (fresh.length === 0) {
      return;
    }
    for (const id of fresh) {
      this.displayedAchievements.add(id);
      this.showAchievement(id);
    }
  }

  private showAchievement(id: string): void {
    const meta = ACHIEVEMENT_DETAILS[id] ?? DEFAULT_ACHIEVEMENT;
    this.toaster.show({ title: meta.title, description: meta.description });
    this.announce(`Achievement unlocked: ${meta.title}.`);
  }
}

function cloneSettings(settings: GameSettings): GameSettings {
  return {
    spawner: { ...settings.spawner },
    engine: { ...settings.engine },
    score: {
      ...settings.score,
      comboThresholds: [...settings.score.comboThresholds],
    },
  };
}

function requirePreset(key: DifficultyKey): DifficultyPreset {
  const preset = PRESET_BY_KEY.get(key);
  if (!preset) {
    throw new Error(`Unknown difficulty preset: ${key}`);
  }
  return preset;
}
